/**
 * Admin → Custom Roles
 *
 * Phase 5b item #5 (2026-05-17). Tenant-authored RBAM roles composed
 * from the platform's permission catalogue and a scope envelope
 * (BU × system × data class × purpose), with a 2-person approval gate
 * (creator can never self-approve; CHECK constraint at DB level + 403
 * guard in the service layer).
 *
 * Layout: 2-column page -
 *   Left:  Wizard for drafting a new role (name + description +
 *          permission picker + scope envelope inputs)
 *   Right: List of existing roles with approve/revoke actions per status
 *
 * The runtime evaluator for actorRole=custom_role at request time is
 * tracked as a follow-up Phase 7-ish task (rbam-real-engine.ts still
 * returns explicit deny for custom_role); but the authoring + approval
 * flow ships standalone here so the wizard surface is fully functional.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  ShieldCheck,
  Plus,
  Ban,
  KeyRound,
  Users as UsersIcon,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listCustomRoles,
  createCustomRole,
  approveCustomRole,
  revokeCustomRole,
  type TenantCustomRole,
  type CustomRoleStatus,
} from '@/lib/api';

const STATUS_VARIANT: Record<CustomRoleStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  draft: 'warning',
  approved: 'success',
  revoked: 'critical',
};
const STATUS_ICON: Record<CustomRoleStatus, typeof CheckCircle2> = {
  draft: Clock,
  approved: ShieldCheck,
  revoked: XCircle,
};

// Common permission picks - operators check boxes to compose a grant set.
// These mirror the @foctta/rbam-engine PLACEHOLDER_PERMISSION_MATRIX
// shape (surface:resource[:action]). Wildcards are allowed but discouraged
// for custom roles (use scoped grants).
const PERMISSION_CATALOG: ReadonlyArray<{
  surface: string;
  permissions: ReadonlyArray<{ key: string; label: string }>;
}> = [
  {
    surface: 'Discovery',
    permissions: [
      { key: 'discovery:read', label: 'Read findings, assets, scans' },
      { key: 'discovery:write', label: 'Triage findings, update status' },
      { key: 'discovery:classify', label: 'Trigger classification jobs' },
    ],
  },
  {
    surface: 'Activity',
    permissions: [
      { key: 'activity:read', label: 'Read activity events + UEBA' },
      { key: 'activity:write', label: 'Annotate events, ack alerts' },
    ],
  },
  {
    surface: 'Detection (DDR)',
    permissions: [
      { key: 'detection:read', label: 'Read decisions + rules' },
      { key: 'detection:write', label: 'Edit decisions (live tune)' },
      { key: 'detection:rules:draft', label: 'Draft new rules' },
      { key: 'detection:rules:approve', label: 'Approve rule changes' },
      { key: 'detection:playbook:run', label: 'Run playbooks' },
      { key: 'detection:kill_session', label: 'Kill active session' },
      { key: 'detection:quarantine', label: 'Quarantine asset' },
    ],
  },
  {
    surface: 'Frontier',
    permissions: [
      { key: 'frontier:read', label: 'Read frontier modules' },
      { key: 'frontier:replay:run', label: 'Run Compliance Replay' },
      { key: 'frontier:sensor:register', label: 'Register eBPF / network-tap sensor' },
      { key: 'frontier:byo_llm:write', label: 'Configure BYO-LLM (Tier 5)' },
    ],
  },
  {
    surface: 'Platform / Admin',
    permissions: [
      { key: 'platform:connectors:read', label: 'Read connector config' },
      { key: 'platform:connectors:write', label: 'Edit connector config' },
      { key: 'platform:kms:read', label: 'Read KMS bindings' },
      { key: 'platform:rbam:read', label: 'Read RBAM matrix' },
      { key: 'platform:sso:read', label: 'Read SSO/SCIM config' },
    ],
  },
];

export function AdminCustomRoles(): JSX.Element {
  const [roles, setRoles] = useState<TenantCustomRole[]>([]);
  const [counts, setCounts] = useState({ draft: 0, approved: 0, revoked: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [scopeBu, setScopeBu] = useState<'all' | 'narrow'>('all');
  const [scopeBuList, setScopeBuList] = useState('');
  const [scopeSystem, setScopeSystem] = useState<'all' | 'narrow'>('all');
  const [scopeSystemList, setScopeSystemList] = useState('');
  const [scopeDataClass, setScopeDataClass] = useState<'all' | 'narrow'>('all');
  const [scopeDataClassList, setScopeDataClassList] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listCustomRoles({});
      setRoles(r.customRoles);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load custom roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  function togglePerm(key: string): void {
    const next = new Set(selectedPerms);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedPerms(next);
  }

  function buildScopeEnvelope(): Record<string, 'all' | string[]> {
    const env: Record<string, 'all' | string[]> = {};
    if (scopeBu === 'all') env.businessUnit = 'all';
    else {
      const list = scopeBuList.split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length > 0) env.businessUnit = list;
    }
    if (scopeSystem === 'all') env.system = 'all';
    else {
      const list = scopeSystemList.split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length > 0) env.system = list;
    }
    if (scopeDataClass === 'all') env.dataClass = 'all';
    else {
      const list = scopeDataClassList.split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length > 0) env.dataClass = list;
    }
    return env;
  }

  async function handleSubmit(): Promise<void> {
    if (!name.trim() || selectedPerms.size === 0) {
      setError('Name and at least one permission are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createCustomRole({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        permissions: [...selectedPerms],
        scopeEnvelope: buildScopeEnvelope(),
      });
      setName('');
      setDescription('');
      setSelectedPerms(new Set());
      setScopeBu('all');
      setScopeBuList('');
      setScopeSystem('all');
      setScopeSystemList('');
      setScopeDataClass('all');
      setScopeDataClassList('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to create custom role');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove(role: TenantCustomRole): Promise<void> {
    const reason = window.prompt(
      `Approve custom role "${role.name}"?\nApproval reason (e.g. "Verified scope envelope; permissions match RoPA item P-42"):`,
    );
    if (!reason) return;
    try {
      await approveCustomRole(role.id, reason);
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'approve failed';
      if (msg.includes('self_approval_forbidden')) {
        setError(
          'You cannot approve a custom role you created. The 2-person rule requires a different actor (DPO or IT Admin).',
        );
      } else {
        setError(msg);
      }
    }
  }

  async function handleRevoke(role: TenantCustomRole): Promise<void> {
    const reason = window.prompt(`Revoke custom role "${role.name}"?\nRevocation reason:`);
    if (!reason) return;
    try {
      await revokeCustomRole(role.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'revoke failed');
    }
  }

  const sortedRoles = useMemo(() => {
    // Drafts at top (need attention), then approved, then revoked.
    const order: Record<CustomRoleStatus, number> = { draft: 0, approved: 1, revoked: 2 };
    return [...roles].sort((a, b) => {
      const diff = order[a.status] - order[b.status];
      if (diff !== 0) return diff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [roles]);

  return (
    <PageContainer>
      <PageHeader
        title="Custom Roles"
        subtitle="Tenant-authored RBAM roles with 2-person approval. Compose permission grants + scope envelope (BU × system × data class × purpose). Creator + DPO/IT-Admin co-approver enforced."
        badges={[
          { label: '2-person rule', variant: 'glass' },
          { label: 'RBAM scope envelope', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Custom role action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Awaiting approval" value={counts.draft} icon={Clock} glow={counts.draft > 0 ? 'medium' : 'low'} hint="needs DPO/IT-Admin" />
        <KpiCard label="Approved" value={counts.approved} icon={ShieldCheck} glow={counts.approved > 0 ? 'high' : 'low'} hint="active" />
        <KpiCard label="Revoked" value={counts.revoked} icon={XCircle} hint="archived" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Wizard */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Draft new custom role
            </CardTitle>
            <CardDescription>
              You can never approve your own draft (2-person rule enforced server-side at both
              service and CHECK-constraint level). After saving, a different DPO or IT Admin will
              see it in the approval queue.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="BFSI L2 Reviewer"
                maxLength={120}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Approve rule changes for BFSI BUs; no edit rights"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <div className="text-muted-foreground mb-1">
                Permissions ({selectedPerms.size} selected)
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {PERMISSION_CATALOG.map((group) => (
                  <div key={group.surface} className="glass-lite rounded p-2">
                    <div className="text-[11px] font-medium mb-1">{group.surface}</div>
                    {group.permissions.map((p) => (
                      <label key={p.key} className="flex items-start gap-2 cursor-pointer py-0.5">
                        <input
                          type="checkbox"
                          checked={selectedPerms.has(p.key)}
                          onChange={() => togglePerm(p.key)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <code className="font-mono text-[10px]">{p.key}</code>
                          <div className="text-[10px] text-muted-foreground">{p.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Scope envelope</div>
              <div className="space-y-2">
                {([
                  ['Business Unit', scopeBu, setScopeBu, scopeBuList, setScopeBuList],
                  ['System', scopeSystem, setScopeSystem, scopeSystemList, setScopeSystemList],
                  ['Data class', scopeDataClass, setScopeDataClass, scopeDataClassList, setScopeDataClassList],
                ] as const).map(([label, mode, setMode, list, setList]) => (
                  <div key={label} className="glass-lite rounded p-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[10px] font-medium">{label}</div>
                      <select
                        value={mode}
                        onChange={(e) => setMode(e.target.value as 'all' | 'narrow')}
                        className="text-[10px] rounded px-1 py-0.5 bg-background/40 border border-border/40"
                      >
                        <option value="all">all</option>
                        <option value="narrow">narrow to list</option>
                      </select>
                    </div>
                    {mode === 'narrow' && (
                      <input
                        type="text"
                        value={list}
                        onChange={(e) => setList(e.target.value)}
                        placeholder={`comma-separated ${label.toLowerCase()} values`}
                        className="w-full glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end pt-1">
              <Button
                data-action="disc.adm_custom_roles.a01"
                size="sm"
                onClick={() => void handleSubmit()}
                disabled={submitting || !name.trim() || selectedPerms.size === 0}
              >
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <KeyRound className="h-3.5 w-3.5 mr-1" />}
                Save draft
              </Button>
            </div>
          </div>
        </GlassCard>

        {/* List */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Roles ({roles.length})</CardTitle>
            <CardDescription clamp>Drafts surface first; approve from a different account.</CardDescription>
          </CardHeader>
          {loading && <LoadingState caption="Loading…" />}
          {!loading && sortedRoles.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <UsersIcon className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No custom roles yet. Use the wizard on the left to draft one.</div>
            </div>
          )}
          {!loading && sortedRoles.length > 0 && (
            <div className="space-y-2">
              {sortedRoles.map((r, i) => {
                const SIcon = STATUS_ICON[r.status];
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{r.name}</span>
                          <Badge variant={STATUS_VARIANT[r.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className="h-3 w-3" />
                            {r.status}
                          </Badge>
                        </div>
                        {r.description && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">{r.description}</div>
                        )}
                        <div className="text-[10px] mt-1 flex flex-wrap gap-1">
                          {r.permissions.map((p) => (
                            <code key={p} className="font-mono bg-muted/30 rounded px-1">{p}</code>
                          ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Created by <code className="font-mono">{r.createdByActorId.slice(0, 8)}…</code>
                          {' '} on {new Date(r.createdAt).toLocaleDateString()}
                          {r.approvedByActorId && (
                            <>
                              {' '}· approved by <code className="font-mono">{r.approvedByActorId.slice(0, 8)}…</code>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {r.status === 'draft' && (
                          <Button
                            data-action="disc.adm_custom_roles.a02"
                            size="sm"
                            variant="glass"
                            onClick={() => void handleApprove(r)}
                            title="Approve (must be a different actor than the creator)"
                          >
                            <ShieldCheck className="h-3 w-3" />
                          </Button>
                        )}
                        {r.status === 'approved' && (
                          <Button
                            data-action="disc.adm_custom_roles.a03"
                            size="sm"
                            variant="glass"
                            onClick={() => void handleRevoke(r)}
                            title="Revoke"
                          >
                            <Ban className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>
    </PageContainer>
  );
}
